import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Toggle, Label } from "@/admin/ui";
import { toast } from "sonner";

const IntegrationsAdmin = () => {
  const [s, setS] = useState(null);
  const [f, setF] = useState({});
  const [addrs, setAddrs] = useState("");
  const load = () => api.get("/admin/integrations").then((r) => { setS(r.data); setAddrs((r.data.email_addresses || []).join("\n")); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return <p className="text-espresso/50">Loading…</p>;

  const save = async () => {
    const body = { ...f, email_addresses: addrs.split("\n").map((x) => x.trim()).filter(Boolean),
      email_domain: f.email_domain ?? s.email_domain, email_from_default: f.email_from_default ?? s.email_from_default,
      stripe_publishable: f.stripe_publishable ?? s.stripe_publishable,
      stripe_live_enabled: f.stripe_live_enabled ?? s.stripe_live_enabled,
      auto_reply_enabled: f.auto_reply_enabled ?? s.auto_reply_enabled,
      auto_draft_enabled: f.auto_draft_enabled ?? s.auto_draft_enabled,
      away_holidays: (f.away_holidays ?? (s.away_holidays || []).join("\n")).split("\n").map((x) => x.trim()).filter(Boolean) };
    try { const { data } = await api.put("/admin/integrations", body); setS(data); setF({}); setAddrs((data.email_addresses || []).join("\n")); toast.success("Saved securely"); }
    catch { toast.error("Save failed"); }
  };
  const up = (k, v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <div className="max-w-2xl">
      <PageHead title="Go Live" sub="Your keys are stored securely and never shown in the browser.">
        <Btn onClick={save} testid="admin-save">Save</Btn>
      </PageHead>

      <Card className="mb-4">
        <h2 className="font-serif text-2xl mb-1">Payments — Stripe</h2>
        <p className="text-xs text-espresso/50 mb-4">Paste your LIVE keys, then switch on. Until then, test mode is used.</p>
        <Toggle label={`Live payments ${s.stripe_live_enabled ? "ON" : "OFF"}`} value={f.stripe_live_enabled ?? s.stripe_live_enabled} onChange={(v) => up("stripe_live_enabled", v)} />
        <Field label={`Live Secret Key ${s.stripe_secret_set ? "(set — leave blank to keep)" : ""}`} value={f.stripe_live_secret || ""} onChange={(v) => up("stripe_live_secret", v)} placeholder="sk_live_…" />
        <Field label="Live Publishable Key" value={f.stripe_publishable ?? s.stripe_publishable} onChange={(v) => up("stripe_publishable", v)} placeholder="pk_live_…" />
        <Field label={`Live Webhook Secret ${s.stripe_webhook_set ? "(set)" : ""}`} value={f.stripe_live_webhook || ""} onChange={(v) => up("stripe_live_webhook", v)} placeholder="whsec_…" />
      </Card>

      <Card>
        <h2 className="font-serif text-2xl mb-1">Email — Resend</h2>
        <p className="text-xs text-espresso/50 mb-4">Add your Resend API key + verified domain to send/receive from your own addresses.</p>
        <Field label={`Resend API Key ${s.resend_key_set ? "(set — leave blank to keep)" : ""}`} value={f.resend_api_key || ""} onChange={(v) => up("resend_api_key", v)} placeholder="re_…" />
        <Field label={`Inbound Webhook Secret ${s.resend_webhook_set ? "(set)" : ""}`} value={f.resend_webhook_secret || ""} onChange={(v) => up("resend_webhook_secret", v)} placeholder="whsec_…" />
        <Field label="Your email domain" value={f.email_domain ?? s.email_domain} onChange={(v) => up("email_domain", v)} placeholder="mydomain.com" />
        <Field label="Default From address" value={f.email_from_default ?? s.email_from_default} onChange={(v) => up("email_from_default", v)} placeholder="hello@mydomain.com" />
        <Label>Sender addresses (one per line)</Label>
        <textarea value={addrs} onChange={(e) => setAddrs(e.target.value)} rows={4} placeholder={"hello@mydomain.com\nsales@mydomain.com\nenquiries@mydomain.com"}
          className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
        <p className="text-xs text-espresso/50 mt-3">Inbound URL for Resend: <code>/api/email/inbound</code> — point your domain's inbound routing here to fill your Inbox.</p>
        <div className="mt-5 pt-4 border-t border-espresso/10">
          <Toggle label={`Auto-reply to new enquiries ${(f.auto_reply_enabled ?? s.auto_reply_enabled) ? "ON" : "OFF"}`}
            value={f.auto_reply_enabled ?? s.auto_reply_enabled} onChange={(v) => up("auto_reply_enabled", v)} />
          <p className="text-xs text-espresso/50 mt-1">Anyone who emails your default address gets an instant branded acknowledgement.</p>
          <div className="mt-4">
            <Toggle label={`Pre-draft a reply for review ${(f.auto_draft_enabled ?? s.auto_draft_enabled) ? "ON" : "OFF"}`}
              value={f.auto_draft_enabled ?? s.auto_draft_enabled} onChange={(v) => up("auto_draft_enabled", v)} />
            <p className="text-xs text-espresso/50 mt-1">The assistant writes a suggested reply the moment mail lands — waiting in the thread for you to review and send.</p>
          </div>
        </div>
      </Card>

      <Card className="mt-4">
        <h2 className="font-serif text-2xl mb-1">Email signature</h2>
        <p className="text-xs text-espresso/50 mb-4">Added automatically to the end of every reply you send.</p>
        <textarea data-testid="email-signature" value={f.email_signature ?? s.email_signature} onChange={(e) => up("email_signature", e.target.value)} rows={4}
          placeholder={"Nior Amara\nNoirbyAmara\nGlasgow, UK"}
          className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
      </Card>

      <Card className="mt-4">
        <h2 className="font-serif text-2xl mb-1">Away message</h2>
        <p className="text-xs text-espresso/50 mb-4">When on, out-of-hours enquiries get this reply instead of the usual acknowledgement (Glasgow time).</p>
        <Toggle label={`Away replies ${(f.away_enabled ?? s.away_enabled) ? "ON" : "OFF"}`} value={f.away_enabled ?? s.away_enabled} onChange={(v) => up("away_enabled", v)} />
        <textarea data-testid="away-message" value={f.away_message ?? s.away_message} onChange={(e) => up("away_message", e.target.value)} rows={3}
          placeholder="Thank you for writing. Our atelier is currently closed and will respond when we reopen."
          className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso mt-2 mb-4" />
        <div className="flex gap-4">
          <div className="flex-1"><Field label="Open from (hour, 0–23)" value={String(f.away_start ?? s.away_start)} onChange={(v) => up("away_start", v)} /></div>
          <div className="flex-1"><Field label="Open until (hour, 0–23)" value={String(f.away_end ?? s.away_end)} onChange={(v) => up("away_end", v)} /></div>
        </div>
        <Toggle label={`Away on weekends ${(f.away_weekends ?? s.away_weekends) ? "ON" : "OFF"}`} value={f.away_weekends ?? s.away_weekends} onChange={(v) => up("away_weekends", v)} />
        <Label>Holiday dates (YYYY-MM-DD, one per line)</Label>
        <textarea data-testid="away-holidays" value={f.away_holidays ?? (s.away_holidays || []).join("\n")} onChange={(e) => up("away_holidays", e.target.value)} rows={3}
          placeholder={"2026-12-25\n2026-12-26\n2027-01-01"}
          className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
      </Card>
    </div>
  );
};

export default IntegrationsAdmin;
